import { createContext, useContext, useMemo, type ReactNode } from "react";

import { DEFAULT_TENANT, type Tenant } from "@/lib/tenants";
import { useTenants } from "@/hooks/useTenants";

/**
 * Current tenant, resolved from `window.location.host` against the live
 * tenants list (loaded by TenantsProvider from Lovable Cloud, with a
 * localStorage fallback for cold loads).
 *
 * Override for staging / preview: append `?tenant=<id>` to any URL, the
 * provider checks the query string first and picks that tenant if it
 * matches a loaded id. Useful for previewing a future city's UX on a
 * staging URL before its real domain is live. Not exposed in any UI.
 */

const TenantCtx = createContext<Tenant>(DEFAULT_TENANT);

/** Where a preview host remembers its `?tenant=` choice. */
export const TENANT_OVERRIDE_KEY = "tenant-override";

/**
 * The tenant for a host and query string. On a host no tenant owns (the
 * workers.dev preview, localhost, Lovable previews) a `?tenant=` choice is
 * remembered, so navigating, signing in or reloading without the parameter
 * stays on that tenant instead of silently falling back to the first one
 * (which once saved a Nosara test listing under Santa Teresa). A tenant's
 * own domain always decides by host.
 */
export const pickTenant = (
  tenants: Tenant[],
  { host, search, stored }: { host: string; search: string; stored: string | null },
): { tenant: Tenant; remember: string | null } => {
  const normalized = host.toLowerCase().replace(/:\d+$/, "");
  const byHost = tenants.find((t) => t.hosts.includes(normalized));
  const override = new URLSearchParams(search).get("tenant");
  const overridden = override ? tenants.find((t) => t.id === override) : undefined;
  if (overridden) return { tenant: overridden, remember: byHost ? null : overridden.id };
  if (byHost) return { tenant: byHost, remember: null };
  const remembered = stored ? tenants.find((t) => t.id === stored) : undefined;
  return { tenant: remembered ?? tenants[0], remember: null };
};

const readStored = (): string | null => {
  try {
    return window.localStorage.getItem(TENANT_OVERRIDE_KEY);
  } catch {
    return null;
  }
};

const resolveTenant = (tenants: Tenant[]): Tenant => {
  if (tenants.length === 0) return DEFAULT_TENANT;
  if (typeof window === "undefined") return tenants[0];
  const { tenant, remember } = pickTenant(tenants, {
    host: window.location.host,
    search: window.location.search,
    stored: readStored(),
  });
  if (remember) {
    try {
      window.localStorage.setItem(TENANT_OVERRIDE_KEY, remember);
    } catch {
      // Private mode or blocked storage: the ?tenant= link still works per page.
    }
  }
  return tenant;
};

export const TenantProvider = ({ children }: { children: ReactNode }) => {
  const { tenants } = useTenants();
  // Recompute when the loaded tenants list changes, handles the cold-load
  // case where we render from cache, then the network resolves a fresh
  // list and the matched tenant should update if it changed.
  const tenant = useMemo(() => resolveTenant(tenants), [tenants]);
  return <TenantCtx.Provider value={tenant}>{children}</TenantCtx.Provider>;
};

export const useTenant = (): Tenant => useContext(TenantCtx);

/**
 * Build an absolute URL under the current tenant's canonical site. Use this
 * anywhere you'd previously have hardcoded `https://www.mysantateresahome.com`
 *, JSON-LD URLs, canonical tags, og:url. Static contexts that can't call a
 * hook (jsonLd builders in src/lib/seo.ts) should accept a `siteUrl` argument
 * derived from `useTenant().siteUrl` at the call site.
 */
export const useAbsUrl = () => {
  const tenant = useTenant();
  return (path: string): string => {
    if (path.startsWith("http")) return path;
    const slash = path.startsWith("/") ? "" : "/";
    return `${tenant.siteUrl}${slash}${path}`;
  };
};
